import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Snackbar,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import { observer } from 'mobx-react-lite';
import { roleApi, userApi } from 'api';
import type { User } from 'types/api';
import * as Yup from 'yup';
import { personalDataStore } from 'stores/personalDataStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from 'routes/paths';
import InputMask from 'react-input-mask';
import axios from 'axios';
import { Icon } from '@iconify/react';
import { panelSx, SectionHeader } from 'components/Profile/profileStyles';
import HelpTooltip from './HelpTooltip';

const MAP_ERRORS = {
  'phone must be a valid phone number':
    'Пожалуйста, укажите корректный номер телефона, в формате +7 (XXX) XXX-XX-XX',
  'email must be a valid email': 'Пожалуйста, укажите корректный email'
};

// Design-system field styling (YYNY.AI / Veilstaff): 8px radius, hairline
// border that turns into a 2px indigo ring on focus and a magenta-pink ring
// on error. Shared by plain inputs, the select and the autocompletes.
const fieldSx = (theme: Theme) => ({
  '& .MuiInputBase-root': {
    borderRadius: `${theme.shape.borderRadius}px`,
    backgroundColor: theme.palette.common.white
  },
  '& .MuiInputBase-input': {
    fontSize: 15
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.grey[500], 0.32),
    transition: theme.transitions.create('border-color', { duration: 120 })
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.grey[500], 0.6)
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: 2
  },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: theme.palette.grey[200],
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.grey[500], 0.24)
    }
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    marginTop: '6px',
    fontSize: 12,
    lineHeight: '18px',
    whiteSpace: 'pre-line'
  }
});

// Label sits tight above the field (design system: 6px gap, h6 weight).
const FieldLabel = ({
  htmlFor,
  required,
  children
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: 'block',
      mb: 0.75,
      fontSize: 14,
      lineHeight: '22px',
      fontWeight: 600,
      color: 'text.primary'
    }}
  >
    {children}
    {required ? (
      <Box component="span" aria-hidden sx={{ color: 'text.disabled', ml: 0.5 }}>
        *
      </Box>
    ) : null}
  </Typography>
);

const UserForm = observer(() => {
  const { userId, user: authUser, userLoading, userError } = personalDataStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const user = authUser;
  const loading = userLoading;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof typeof initialValues, string>>
  >({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [forceAlertOpen, setForceAlertOpen] = useState(false);

  const [roleChoiceModalOpen, setRoleChoiceModalOpen] = useState(false);
  const [roleChoiceValue, setRoleChoiceValue] = useState<'HR' | 'CANDIDATE'>('CANDIDATE');
  const [roleChoiceLoading, setRoleChoiceLoading] = useState(false);
  const [needCheckPosition, setNeedCheckPosition] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const initialValues = useMemo(() => {
    return {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      phone: user?.phone ?? '',
      speciality: user?.speciality ?? '',
      specialityLevel: user?.specialityLevel,
      position: user?.position ?? ''
    };
  }, [user]);

  const [formValues, setFormValues] = useState(initialValues);
  const [specializationInput, setSpecializationInput] = useState('');
  const [positionInput, setPositionInput] = useState('');
  

  const selectedSpecializationId = useMemo(() => {
    const found = personalDataStore.specializations.find((s) => s.name === formValues.speciality);
    return found?.id ? Number(found.id) : null;
  }, [formValues.speciality, personalDataStore.specializations]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (formValues.position && !specializationInput) {
        personalDataStore.fetchSpecializationsByPositionName(formValues.position);
      } else {
        personalDataStore.fetchSpecializations(specializationInput);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [specializationInput, formValues.position]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedSpecializationId) {
        personalDataStore.fetchPositionsBySpecializationId(positionInput, selectedSpecializationId).then(() => {
          if(!needCheckPosition) return
          if(!personalDataStore.positions.find(i => i.name === formValues.position)) {
            setFormValues((prev) => ({ ...prev, position: '' }));
          }
        })
      } else {
        personalDataStore.fetchAllPositions(positionInput);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [positionInput, selectedSpecializationId, needCheckPosition]);

  const validationSchema = Yup.object({
    firstName: Yup.string().trim().required('Укажите имя').max(100, 'Слишком длинное значение'),
    lastName: Yup.string().trim().max(100, 'Слишком длинное значение'),
    email: Yup.string().trim().email('Некорректный email'),
    phone: Yup.string()
      .trim()
      .required('Укажите телефон')
      .matches(/^[+\d][\d\s().-]{9,19}$/, 'Некорректный номер телефона'),
    position: Yup.string().trim().max(150, 'Слишком длинное значение'),
    speciality: Yup.string().trim().max(120, 'Слишком длинное значение'),
    specialityLevel: Yup.mixed<User['specialityLevel']>().oneOf(
      ['JUNIOR', 'MIDDLE', 'SENIOR', null, undefined],
      'Выберите уровень'
    )
  });

  const isDirty = useMemo(() => {
    const keys: Array<keyof typeof initialValues> = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'speciality',
      'specialityLevel',
      'position'
    ];
    return keys.some((key) => formValues[key] !== initialValues[key]);
  }, [formValues, initialValues]);

  useEffect(() => {
    setFormValues(initialValues);
    setSpecializationInput(initialValues.speciality ?? '');
    setPositionInput(initialValues.position ?? '');
  }, [initialValues]);

  useEffect(() => {
    if (userError) {
      setError('Не удалось загрузить профиль пользователя');
    }
  }, [userError]);

  const isForce = useMemo(
    () => location.search.includes('force=true') || !personalDataStore.user?.lastName,
    [location.search]
  );

  useEffect(() => {
    if (isForce) {
      setForceAlertOpen(true);
    }
  }, [isForce]);

  const handleChange = (field: keyof typeof formValues) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSpecialityChange = (_event: unknown, newValue: string | null) => {
    if(formValues.position.length) {
      setNeedCheckPosition(true)
    }
    setFormValues((prev) => ({ ...prev, speciality: newValue || '' }));
    if (fieldErrors.speciality) {
      setFieldErrors((prev) => ({ ...prev, speciality: undefined }));
    }
  };

  const handlePositionChange = (_event: unknown, newValue: string | null) => {
    setFormValues((prev) => ({ ...prev, position: newValue || '' }));
    if (fieldErrors.position) {
      setFieldErrors((prev) => ({ ...prev, position: undefined }));
    }
  };

  const validateField = async (field: keyof typeof formValues) => {
    try {
      await validationSchema.validateAt(field as string, formValues);
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setFieldErrors((prev) => ({ ...prev, [field]: err.message }));
      }
    }
  };

  const handleSave = async () => {
    if (!userId || !user) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      await validationSchema.validate(formValues, { abortEarly: false });
      const payload: Partial<User> = {
        firstName: formValues.firstName || undefined,
        lastName: formValues.lastName || undefined,
        email: formValues.email || undefined,
        phone: formValues.phone || undefined,
        speciality: formValues.speciality,
        specialityLevel: formValues.specialityLevel as User['specialityLevel'],
        position: formValues.position
      };
      await userApi.updateUser(userId, payload);
      setSuccessOpen(true);
      setForceAlertOpen(false);
      if (isForce && user?.role !== 'HR') {
        setRoleChoiceModalOpen(true);
        return;
      } else if (isForce) {
        navigate(PATHS.main);
      }
      await personalDataStore.fetchAndApplyUser(userId);
    } catch (e) {
      if (e instanceof Yup.ValidationError) {
        const nextFieldErrors: Partial<Record<keyof typeof formValues, string>> = {};
        e.inner.forEach((issue) => {
          if (issue.path && !nextFieldErrors[issue.path as keyof typeof formValues]) {
            nextFieldErrors[issue.path as keyof typeof formValues] = issue.message;
          }
        });
        setFieldErrors(nextFieldErrors);
      } else if (axios.isAxiosError(e)) {
        const messagge = Array.isArray(e.response?.data?.message)
          ? e.response?.data?.message
          : [e.response?.data?.message];
        if (messagge.some((i: string) => i.includes('phone'))) {
          setFieldErrors((prev) => ({ ...prev, phone: 'Некорректный номер телефона' }));
          return;
        }
        if (messagge.some((i: string) => i.includes('email'))) {
          setFieldErrors((prev) => ({ ...prev, email: 'Некорректный email' }));
          return;
        }
        if (messagge.some((i: string) => i.includes('телефон'))) {
          setFieldErrors((prev) => ({ ...prev, phone: messagge.join('\n') }));
          return;
        }

        setError(
          messagge.map((i: string) => MAP_ERRORS[i as keyof typeof MAP_ERRORS] || i).join('\n')
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSuccess = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setSuccessOpen(false);
  };

  const handleCloseForceAlert = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setForceAlertOpen(false);
  };

  const handleCloseRoleChoiceModal = () => {
    setRoleChoiceModalOpen(false);
    navigate(PATHS.main);
  };

  const handleRoleChoiceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRoleChoiceValue(e.target.value as 'HR' | 'CANDIDATE');
  };

  const handleConfirmRoleChoice = async () => {
    try {
      setRoleChoiceLoading(true);
      if (roleChoiceValue === 'HR' && user?.role !== 'HR') {
        await roleApi.createRoleRequest({ requestedRole: roleChoiceValue });
      }
      await personalDataStore.refreshUser();
      await personalDataStore.getPayments();
      navigate('/main');
    } catch (e) {
      console.error(e);
      setError('Не удалось запросить роль');
    } finally {
      setRoleChoiceModalOpen(false);
      setRoleChoiceLoading(false);
    }
  };

  const showPositionAutocomplete = useMemo(
    () => formValues.speciality !== 'Другое',
    [formValues.speciality]
  );

  return (
    <>
      <Paper elevation={0} sx={panelSx}>
        <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
          <SectionHeader
            title="Личные данные"
            caption="Эти сведения используются проведения интервью и рассыки результатов на почту."
          />

          <Box component="form" noValidate>
            <Grid container rowSpacing={{ xs: 2.5, sm: 3 }} columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="firstName" required>
                  Имя
                </FieldLabel>
                <TextField
                  id="firstName"
                  fullWidth
                  size="small"
                  placeholder="Иван"
                  value={formValues.firstName}
                  onChange={handleChange('firstName')}
                  onBlur={() => validateField('firstName')}
                  required
                  error={Boolean(fieldErrors.firstName)}
                  helperText={fieldErrors.firstName}
                  inputProps={{ 'aria-label': 'Имя' }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel htmlFor="lastName">Фамилия (опционально)</FieldLabel>
                <TextField
                  id="lastName"
                  fullWidth
                  size="small"
                  placeholder="Иванов"
                  value={formValues.lastName}
                  onChange={handleChange('lastName')}
                  onBlur={() => validateField('lastName')}
                  required
                  error={Boolean(fieldErrors.lastName)}
                  helperText={fieldErrors.lastName}
                  inputProps={{ 'aria-label': 'Фамилия' }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <TextField
                  id="email"
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="name@company.ru"
                  value={formValues.email}
                  onChange={handleChange('email')}
                  onBlur={() => validateField('email')}
                  required
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email}
                  inputProps={{ 'aria-label': 'Email' }}
                  disabled={!isForce && !!user?.email}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldLabel htmlFor="phone" required>
                  Телефон
                </FieldLabel>
                <InputMask
                  mask="+9 (999) 999 99 99"
                  value={formValues.phone}
                  maskChar=" "
                  onChange={handleChange('phone')}
                  onBlur={() => validateField('phone')}
                  disabled={!!user?.phone}
                >
                  {(props) => (
                    <TextField
                      id="phone"
                      size="small"
                      error={Boolean(fieldErrors.phone)}
                      helperText={fieldErrors.phone}
                      inputProps={{ 'aria-label': 'Телефон', inputMode: 'tel' }}
                      fullWidth
                      disabled={!!user?.phone}
                      sx={fieldSx}
                      {...props}
                    />
                  )}
                </InputMask>
              </Grid>
              {!isForce ? (
                <>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-end">
                      <Box sx={{ flex: 1 }}>
                        <FieldLabel htmlFor="speciality" required>
                          Сфера деятельности
                        </FieldLabel>
                        <Autocomplete
                          id="speciality"
                          fullWidth
                          size="small"
                          options={personalDataStore.specializations.map((s) => s.name)}
                          value={formValues.speciality || null}
                          onChange={handleSpecialityChange}
                          inputValue={specializationInput}
                          onInputChange={(_event, newInputValue) =>
                            setSpecializationInput(newInputValue)
                          }
                          filterOptions={() => personalDataStore.specializations.map((s) => s.name)}
                          sx={fieldSx}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Выберите специализацию"
                              required
                              error={Boolean(fieldErrors.speciality)}
                              helperText={fieldErrors.speciality}
                              inputProps={{
                                ...params.inputProps,
                                'aria-label': 'Сфера деятельности'
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              {option}
                            </Box>
                          )}
                          noOptionsText="Сфера деятельности не найдена"
                          clearOnEscape
                        />
                      </Box>
                     <HelpTooltip />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <FieldLabel htmlFor="position" required>
                      Должность
                    </FieldLabel>
                    {showPositionAutocomplete ? (
                      <Autocomplete
                        id="position"
                        fullWidth
                        size="small"
                        options={personalDataStore.positions.map((p) => p.name)}
                        value={formValues.position || null}
                        onChange={handlePositionChange}
                        // disabled={!formValues.speciality}
                        inputValue={positionInput}
                        onInputChange={(_event, newInputValue) => setPositionInput(newInputValue)}
                        filterOptions={(options) => options}
                        sx={fieldSx}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Выберите должность"
                            required
                            error={Boolean(fieldErrors.position)}
                            helperText={fieldErrors.position}
                            inputProps={{
                              ...params.inputProps,
                              'aria-label': 'Должность'
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            {option}
                          </Box>
                        )}
                        noOptionsText="Должность не найдена"
                        clearOnEscape
                      />
                    ) : (
                      <TextField
                        id="position"
                        size="small"
                        placeholder="Укажите должность"
                        disabled={!formValues.speciality}
                        required
                        fullWidth
                        onChange={(e) => {
                          setPositionInput(e.currentTarget.value);
                          handlePositionChange(e, e.currentTarget.value);
                        }}
                        value={positionInput}
                        error={Boolean(fieldErrors.position)}
                        helperText={fieldErrors.position}
                        inputProps={{
                          'aria-label': 'Должность'
                        }}
                        sx={fieldSx}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FieldLabel htmlFor="specialityLevel" required>
                      Уровень
                    </FieldLabel>
                    <TextField
                      id="specialityLevel"
                      select
                      fullWidth
                      size="small"
                      value={formValues.specialityLevel ?? ''}
                      onChange={handleChange('specialityLevel')}
                      onBlur={() => validateField('specialityLevel')}
                      required
                      error={Boolean(fieldErrors.specialityLevel)}
                      helperText={fieldErrors.specialityLevel}
                      inputProps={{ 'aria-label': 'Уровень' }}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => {
                          const labels: Record<string, string> = {
                            JUNIOR: 'Начинающий',
                            MIDDLE: 'С опытом',
                            SENIOR: 'Эксперт'
                          };
                          return value ? (
                            labels[value as string]
                          ) : (
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              Выберите уровень
                            </Box>
                          );
                        }
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="JUNIOR">Начинающий</MenuItem>
                      <MenuItem value="MIDDLE">С опытом</MenuItem>
                      <MenuItem value="SENIOR">Эксперт</MenuItem>
                    </TextField>
                  </Grid>
                </>
              ) : null}
            </Grid>
            {error ? (
              <Alert
                severity="error"
                variant="outlined"
                role="alert"
                sx={{
                  mt: 3,
                  borderRadius: `${theme.shape.borderRadius}px`,
                  whiteSpace: 'pre-line'
                }}
              >
                {error}
              </Alert>
            ) : null}
            {loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Загрузка профиля...
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'grey.300' }} />

        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            px: { sm: 4 },
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' }
          }}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={1.5}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || loading || !user || !isDirty}
              aria-label="Сохранить изменения"
              fullWidth={isMobile}
              startIcon={<Icon icon="mdi:content-save" />}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            {!isForce ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setFormValues(initialValues)}
                disabled={saving || loading}
                aria-label="Отменить изменения"
                fullWidth={isMobile}
                startIcon={<Icon icon="mdi:undo" />}
              >
                Отменить изменения
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Paper>

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        aria-label="Успешное сохранение профиля"
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          sx={{ width: '100%' }}
          variant="filled"
        >
          Изменения сохранены
        </Alert>
      </Snackbar>
      <Snackbar
        open={forceAlertOpen && !saving && !loading}
        autoHideDuration={6000}
        onClose={handleCloseForceAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        aria-label="Требуется указать недостающую информацию"
      >
        <Alert
          onClose={handleCloseForceAlert}
          severity="info"
          sx={{ width: '100%' }}
          variant="filled"
        >
          Чтобы продолжить, заполните все обязательные поля в профиле
        </Alert>
      </Snackbar>
      <Dialog
        open={roleChoiceModalOpen}
        onClose={handleCloseRoleChoiceModal}
        aria-labelledby="role-choice-dialog-title"
        aria-describedby="role-choice-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="role-choice-dialog-title">
          С какой ролью вы хотите пользоваться системой?
        </DialogTitle>
        <DialogContent id="role-choice-dialog-description">
          <FormControl component="fieldset" sx={{ width: '100%', pt: 1 }}>
            <FormLabel id="role-choice-radio-group-label" component="legend" sx={{ mb: 1 }}>
              Выберите роль
            </FormLabel>
            <RadioGroup
              aria-labelledby="role-choice-radio-group-label"
              name="role-choice-radio-group"
              value={roleChoiceValue}
              onChange={handleRoleChoiceChange}
              sx={{ pl: 6, mt: 6 }}
            >
              <FormControlLabel
                value="HR"
                control={<Radio sx={{ mr: 3 }} />}
                label={
                  <Stack spacing={0.5}>
                    <Typography fontWeight={600}>HR специалист(представитель компании)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Использовать систему компании для найма: создавать вакансии, получать
                      бесплатные интервью кандидатов и просматривать результаты интервью и отчёты
                    </Typography>
                  </Stack>
                }
                aria-label="Использовать как HR — создание вакансий и найм"
              />
              <FormControlLabel
                value="CANDIDATE"
                control={<Radio sx={{ mr: 3 }} />}
                label={
                  <Stack spacing={0.5}>
                    <Typography fontWeight={600}>Обычный пользователь</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Пройти интервью для проверки навыков и получить подробный аналитический отчёт
                      с рекомендациями по улучшению навыков.
                    </Typography>
                  </Stack>
                }
                sx={{ mt: 6 }}
                aria-label="Использовать как обычный пользователь — прохождение интервью"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleChoiceModal} color="inherit">
            Закрыть
          </Button>
          <Button
            onClick={handleConfirmRoleChoice}
            variant="contained"
            color="primary"
            disabled={roleChoiceLoading}
          >
            {roleChoiceLoading ? 'Ожидание ответа...' : 'Продолжить'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default UserForm;
